/**
 * ObondoOnboarding — first-visit guided tour for the Obondo Coach feed.
 *
 * Mirrors KimaniOnboarding.jsx's spotlight-overlay pattern. Obondo Coach's
 * cards (Helpful / Not helpful / Mute) are per-item and only exist once
 * the feed has data, so most steps here are centred explainer cards
 * rather than anchored spotlights — only the always-present page header
 * gets a real spotlight ring.
 *
 * Persists completion to localStorage (`md_obondo_onboarded`).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
    Sparkles, ShieldAlert, ThumbsUp, VolumeX, RotateCcw,
    X, ChevronRight, ChevronLeft,
} from "lucide-react";

const STORAGE_KEY = "md_obondo_onboarded";

const STEPS = [
    {
        id: "welcome",
        target: null,
        title: "Meet Obondo Coach",
        description: "Obondo watches your open positions, drawdown, volatility, and the news 24/7. The moment something is worth flagging, it drops a coaching note here — this feed is where every nudge lands.",
        icon: Sparkles,
        position: "center",
    },
    {
        id: "feed",
        target: '[data-testid="obondo-coach-header"]',
        title: "Your coaching feed",
        description: "Each card shows what Obondo noticed and why — from daily drawdown warnings and volatility spikes to post-trade reviews and market-intel context.",
        icon: ShieldAlert,
        position: "below",
    },
    {
        id: "rate",
        target: null,
        title: "Rate every nudge",
        description: "Tap Helpful or Not helpful on any card so Obondo learns which coaching types actually help you trade better.",
        icon: ThumbsUp,
        position: "center",
    },
    {
        id: "mute",
        target: null,
        title: "Mute the noisy ones",
        description: "Find a category too frequent or not useful? Tap Mute this type. Bring it back any time from the 'Muted coaching types' strip at the top of the feed.",
        icon: VolumeX,
        position: "center",
    },
    {
        id: "learn",
        target: null,
        title: "Obondo learns you",
        description: "The more you rate, the sharper Obondo's nudges get — tuned to how YOU trade, not a generic template.",
        icon: RotateCcw,
        position: "center",
    },
];

export default function ObondoOnboarding({ onComplete }) {
    const [step, setStep] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const overlayRef = useRef(null);

    const current = STEPS[step];
    const isLast = step === STEPS.length - 1;

    const measureTarget = useCallback(() => {
        if (!current?.target) {
            setTargetRect(null);
            return;
        }
        const el = document.querySelector(current.target);
        if (el) {
            const r = el.getBoundingClientRect();
            setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        } else {
            setTargetRect(null);
        }
    }, [current]);

    useEffect(() => {
        measureTarget();
        window.addEventListener("resize", measureTarget);
        window.addEventListener("scroll", measureTarget, true);
        return () => {
            window.removeEventListener("resize", measureTarget);
            window.removeEventListener("scroll", measureTarget, true);
        };
    }, [measureTarget]);

    const finish = useCallback(() => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
        onComplete?.();
    }, [onComplete]);

    const next = () => {
        if (isLast) { finish(); return; }
        setStep((s) => s + 1);
    };

    const prev = () => setStep((s) => Math.max(0, s - 1));

    const Icon = current.icon;

    const getTooltipStyle = () => {
        if (current.position === "center" || !targetRect) {
            return {
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 101,
            };
        }
        const pad = 12;
        const style = { position: "fixed", zIndex: 101 };
        if (current.position === "below") {
            style.top = targetRect.top + targetRect.height + pad;
            style.left = Math.max(16, targetRect.left + targetRect.width / 2 - 160);
        }
        return style;
    };

    return (
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[100]"
            data-testid="obondo-onboarding-overlay"
        >
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
                <defs>
                    <mask id="obondo-spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {targetRect && (
                            <rect
                                x={targetRect.left - 6}
                                y={targetRect.top - 6}
                                width={targetRect.width + 12}
                                height={targetRect.height + 12}
                                rx="8"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.65)"
                    mask="url(#obondo-spotlight-mask)"
                />
            </svg>

            {targetRect && (
                <div
                    className="absolute rounded-lg border-2 border-primary shadow-[0_0_0_4px_rgba(146,37,41,0.2)] transition-all duration-300"
                    style={{
                        top: targetRect.top - 6,
                        left: targetRect.left - 6,
                        width: targetRect.width + 12,
                        height: targetRect.height + 12,
                        pointerEvents: "none",
                    }}
                />
            )}

            <div
                className="w-[320px] rounded-xl border border-border shadow-2xl p-5 bg-card text-card-foreground animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
                style={getTooltipStyle()}
                data-testid="obondo-onboarding-tooltip"
            >
                <button
                    onClick={finish}
                    className="absolute top-3 right-3 p-1 rounded-md hover:bg-secondary/60 transition-colors text-muted-foreground hover:text-foreground"
                    data-testid="obondo-onboarding-skip"
                    aria-label="Skip tour"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#922529" }}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-sm font-bold">{current.title}</h3>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed mb-4">{current.description}</p>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        {STEPS.map((_, i) => (
                            <div
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                    i === step ? "bg-primary" : i < step ? "bg-primary/40" : "bg-border"
                                }`}
                            />
                        ))}
                        <span className="text-[10px] text-muted-foreground ml-1.5">
                            {step + 1}/{STEPS.length}
                        </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                        {step > 0 && (
                            <button
                                onClick={prev}
                                className="h-7 px-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors inline-flex items-center gap-1"
                                data-testid="obondo-onboarding-prev"
                            >
                                <ChevronLeft className="w-3 h-3" /> Back
                            </button>
                        )}
                        <button
                            onClick={next}
                            className="h-7 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                            style={{ background: "#922529", color: "#fff" }}
                            data-testid="obondo-onboarding-next"
                        >
                            {isLast ? "Got it" : "Next"} <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Check if onboarding has been completed */
export function hasCompletedObondoOnboarding() {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
}
